import React, { createContext, useContext, useState, useCallback } from "react";
import { extractUserFromToken } from "../helpers/auth";
import { validateUserRole } from "../helpers/helpers";
import { userRoles } from "../helpers/constant";

/**
 * Feedback Context
 * Manages state for the entire feedback management system
 */
const FeedbackContext = createContext();

/**
 * Custom hook to use Feedback Context
 * @returns {Object} Feedback context value
 */
export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("useFeedback must be used within a FeedbackProvider");
  }
  return context;
};

/**
 * Feedback Provider Component
 * Wraps feedback management components to provide shared state
 */
export const FeedbackProvider = ({ children }) => {
  // Get current user info
  const currentUser = extractUserFromToken();
  const isSuperAdmin = validateUserRole(userRoles.SUPERADMIN);
  const isAdmin = validateUserRole(userRoles.ADMIN);

  // Analytics State
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // Feedback List State
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackPagination, setFeedbackPagination] = useState({
    total: 0,
    has_more: false,
    next_cursor: null,
  });

  // Incident List State
  const [incidentList, setIncidentList] = useState([]);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [incidentError, setIncidentError] = useState(null);
  const [incidentPagination, setIncidentPagination] = useState({
    total: 0,
    has_more: false,
    next_cursor: null,
  });

  // Category Management State
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  // Filter States
  const [feedbackFilters, setFeedbackFilters] = useState({
    feedbackType: "",
    category: "",
    isAddressed: null,
    startDate: null,
    endDate: null,
    facilityId: null,
    limit: 20,
  });

  const [incidentFilters, setIncidentFilters] = useState({
    status: "",
    assignedUserId: "",
    facilityId: null,
    limit: 20,
  });

  // Selection States
  const [selectedFeedback, setSelectedFeedback] = useState([]);
  const [selectedIncidents, setSelectedIncidents] = useState([]);

  // Modal States
  const [showCreateIncidentModal, setShowCreateIncidentModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Global refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /**
   * Utility functions
   */
  const triggerRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const clearErrors = useCallback(() => {
    setStatsError(null);
    setFeedbackError(null);
    setIncidentError(null);
    setCategoriesError(null);
  }, []);

  const resetFilters = useCallback(() => {
    setFeedbackFilters({
      feedbackType: "",
      category: "",
      isAddressed: null,
      startDate: null,
      endDate: null,
      facilityId: null,
      limit: 20,
    });
    setIncidentFilters({
      status: "",
      assignedUserId: "",
      facilityId: null,
      limit: 20,
    });
  }, []);

  /**
   * Selection management
   */
  const toggleFeedbackSelection = useCallback((feedbackId) => {
    setSelectedFeedback((prev) =>
      prev.includes(feedbackId)
        ? prev.filter((id) => id !== feedbackId)
        : [...prev, feedbackId]
    );
  }, []);

  const selectAllFeedback = useCallback((feedbackIds) => {
    setSelectedFeedback(feedbackIds);
  }, []);

  const clearFeedbackSelection = useCallback(() => {
    setSelectedFeedback([]);
  }, []);

  /**
   * Modal management
   */
  const openCreateIncidentModal = useCallback((preSelectedFeedback = []) => {
    if (preSelectedFeedback.length > 0) {
      setSelectedFeedback(preSelectedFeedback);
    }
    setShowCreateIncidentModal(true);
  }, []);

  const closeCreateIncidentModal = useCallback(() => {
    setShowCreateIncidentModal(false);
    // Don't clear selected feedback - user might want to keep selection
  }, []);

  const openEditCategoryModal = useCallback((category) => {
    setEditingCategory(category);
    setShowEditCategoryModal(true);
  }, []);

  const closeEditCategoryModal = useCallback(() => {
    setShowEditCategoryModal(false);
    setEditingCategory(null);
  }, []);

  /**
   * Permission helpers
   */
  const canCreateIncidents = isSuperAdmin;
  const canManageCategories = isSuperAdmin;
  const canViewAllFacilities = isSuperAdmin;
  const canReassignIncidents = isSuperAdmin || isAdmin;

  /**
   * Context value
   */
  const value = {
    // User info
    currentUser,
    isSuperAdmin,
    isAdmin,

    // Analytics state
    feedbackStats,
    setFeedbackStats,
    statsLoading,
    setStatsLoading,
    statsError,
    setStatsError,

    // Feedback list state
    feedbackList,
    setFeedbackList,
    feedbackLoading,
    setFeedbackLoading,
    feedbackError,
    setFeedbackError,
    feedbackPagination,
    setFeedbackPagination,

    // Incident list state
    incidentList,
    setIncidentList,
    incidentLoading,
    setIncidentLoading,
    incidentError,
    setIncidentError,
    incidentPagination,
    setIncidentPagination,

    // Category state
    categories,
    setCategories,
    categoriesLoading,
    setCategoriesLoading,
    categoriesError,
    setCategoriesError,

    // Filter state
    feedbackFilters,
    setFeedbackFilters,
    incidentFilters,
    setIncidentFilters,

    // Selection state
    selectedFeedback,
    setSelectedFeedback,
    selectedIncidents,
    setSelectedIncidents,

    // Modal state
    showCreateIncidentModal,
    setShowCreateIncidentModal,
    showEditCategoryModal,
    setShowEditCategoryModal,
    editingCategory,
    setEditingCategory,

    // Global state
    refreshTrigger,

    // Utility functions
    triggerRefresh,
    clearErrors,
    resetFilters,

    // Selection functions
    toggleFeedbackSelection,
    selectAllFeedback,
    clearFeedbackSelection,

    // Modal functions
    openCreateIncidentModal,
    closeCreateIncidentModal,
    openEditCategoryModal,
    closeEditCategoryModal,

    // Permission flags
    canCreateIncidents,
    canManageCategories,
    canViewAllFacilities,
    canReassignIncidents,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};

export default FeedbackContext;
